import React, { createContext, useState, useContext, useEffect } from "react";
import { Amplify } from "aws-amplify";
import { signIn, signOut, signUp, confirmSignUp, resetPassword, confirmResetPassword, getCurrentUser, fetchAuthSession } from "@aws-amplify/auth";
import authConfig from "../config/auth.json";

// Configure Amplify
Amplify.configure({
	Auth: {
		Cognito: {
			userPoolId: authConfig.userPoolId,
			userPoolClientId: authConfig.userPoolWebClientId,
			loginWith: {
				oauth: {
					domain: authConfig.oauth.domain,
					scopes: authConfig.oauth.scope,
					redirectSignIn: authConfig.oauth.redirectSignIn.split(","),
					redirectSignOut: authConfig.oauth.redirectSignOut.split(","),
					responseType: authConfig.oauth.responseType,
				},
			},
		},
	},
});

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		checkAuthState();
	}, []);

	const checkAuthState = async () => {
		try {
			const user = await getCurrentUser();
			const session = await fetchAuthSession();
			console.log("user", user);
			setUser(user);
			setIsAuthenticated(session.tokens !== undefined);
		} catch (error) {
			setUser(null);
			setIsAuthenticated(false);
		}
		setLoading(false);
	};

	const login = async (email, password) => {
		try {
			if (isAuthenticated) {
				await signOut();
				setUser(null);
				setIsAuthenticated(false);
			}

			const { isSignedIn } = await signIn({ username: email, password });
			if (isSignedIn) {
				await checkAuthState();
				return true;
			}
			return false;
		} catch (error) {
			console.error("Error signing in:", error);
			return false;
		}
	};

	const logout = async () => {
		try {
			await signOut();
			setUser(null);
			setIsAuthenticated(false);
		} catch (error) {
			console.error("Error signing out:", error);
		}
	};

	const handleSignUp = async (email, password, name) => {
		try {
			await signUp({
				username: email,
				password,
				options: {
					userAttributes: {
						email,
						name,
					},
				},
			});
			return true;
		} catch (error) {
			console.error("Error signing up:", error);
			return false;
		}
	};

	const handleConfirmSignUp = async (email, code) => {
		try {
			await confirmSignUp({
				username: email,
				confirmationCode: code,
			});
			return true;
		} catch (error) {
			console.error("Error confirming sign up:", error);
			return false;
		}
	};

	const handleForgotPassword = async (email) => {
		try {
			await resetPassword({ username: email });
			return true;
		} catch (error) {
			console.error("Error initiating password reset:", error);
			return false;
		}
	};

	const handleResetPassword = async (email, code, newPassword) => {
		try {
			await confirmResetPassword({
				username: email,
				confirmationCode: code,
				newPassword,
			});
			return true;
		} catch (error) {
			console.error("Error resetting password:", error);
			return false;
		}
	};

	if (loading) {
		return null; // or a loading spinner
	}

	return (
		<AuthContext.Provider
			value={{
				isAuthenticated,
				user,
				login,
				logout,
				signUp: handleSignUp,
				confirmSignUp: handleConfirmSignUp,
				forgotPassword: handleForgotPassword,
				resetPassword: handleResetPassword,
			}}>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};
